/**
 * Pinia store da identidade do Companion (L1-L3 da Fase 5).
 *
 * Persiste em localStorage. NÃO é parte do vault criptografado —
 * é preferência de UX (Princípio XI: Companion é relação, não LLM).
 *
 * Constitucional:
 * - Princípio XI: este store é puramente UX. Nenhuma chave de LLM
 *   passa por aqui.
 * - P-UX-01: tudo opt-in; defaults sensatos.
 * - P-UX-10: voz é opt-in (não automática).
 */
import { defineStore } from "pinia";
import { ref } from "vue";
import {
  DEFAULT_IDENTITY,
  type CompanionIdentity,
  type CompanionVisualId,
} from "./types";

const STORAGE_KEY = "hlos.companion.identity.v1";

function loadFromStorage(): CompanionIdentity {
  if (typeof localStorage === "undefined") return { ...DEFAULT_IDENTITY };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_IDENTITY };
    return { ...DEFAULT_IDENTITY, ...JSON.parse(raw) };
  } catch {
    return { ...DEFAULT_IDENTITY };
  }
}

function persist(value: CompanionIdentity): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // ignore
  }
}

export const useCompanionIdentityStore = defineStore(
  "companion-identity",
  () => {
    const identity = ref<CompanionIdentity>(loadFromStorage());

    function setName(name: string): void {
      const cleaned = name.trim().slice(0, 32);
      identity.value = { ...identity.value, name: cleaned };
      persist(identity.value);
    }

    function setVisual(visual: CompanionVisualId): void {
      identity.value = { ...identity.value, visual };
      persist(identity.value);
    }

    function reset(): void {
      identity.value = { ...DEFAULT_IDENTITY };
      persist(identity.value);
    }

    /**
     * Como o Companion é chamado na UI.
     * Se não tem nome, usa 'Companion' (genérico, sem provedor).
     */
    const displayName = (): string => {
      return identity.value.name.trim() || "Companion";
    };

    return { identity, setName, setVisual, reset, displayName };
  },
);
